import { __awaiter, __generator } from "tslib";
import * as AugLoopUtils from "./utils/AugLoopUtils";
import { encodeWAVWithoutHeader, AUDIO_SAMPLE_RATE, AUDIO_BITS_PER_SAMPLE, } from "./utils/AudioUtils";
import { DictationEventsType, DictationSettingsManager, VoiceResponseVersion, SpeechToTextProfile, } from "@augloop-types/voice";
import { ULS, TraceLevel, LogCategory } from "@1js/office-online-telemetry";
import { MicrophoneError } from "./enum/MicrophoneError";
import { getBooleanFeatureGate } from "@1js/office-online-app-platform";
import { stringifyException } from "./utils/AugLoopUtils";
import { AudioStreamPerfTracker } from "./AudioStreamPerfTracker";
var dictationUserPerceivedLatencies = function () {
    return getBooleanFeatureGate("Microsoft.Office.WordOnline.DictationUserPerceivedLatencies", 
    /*defaultValue*/ false) ||
        getBooleanFeatureGate("Microsoft.Office.OneNoteOnline.DictationUserPerceivedLatencies", 
        /*defaultValue*/ false);
};
// Re-export augloop types exposed in our interfaces so callers don't have to depend directly on augloop packages.
export { SpeechToTextProfile, DictationEventsType };
function getDecodeAudioDataPromise(arrayBuffer, audioContext) {
    return new Promise(function (resolve, reject) {
        audioContext.decodeAudioData(arrayBuffer, resolve, reject);
    });
}
/** Represents a session where we send voice data to AL, effectively the client for sending audio to workflow:voice. */
var VoiceSession = /** @class */ (function () {
    function VoiceSession(runtimeManager, fdSpeechClientObserver, useFrontdoorWorkflow, commandTelemetry, sessionTelemetry, speechToTextProfile) {
        if (useFrontdoorWorkflow === void 0) { useFrontdoorWorkflow = false; }
        if (speechToTextProfile === void 0) { speechToTextProfile = SpeechToTextProfile.Dictation; }
        this.audioContext = null;
        this.microphoneSessionState = null;
        this.responseVersion = VoiceResponseVersion.version2;
        this.fileBytes = null; // When sending a file, contains the data we will send to AL.
        this._wavFileAudioChunkSize = 16000; // 11888 bytes is Audio Chunk Size for Microphone
        this.endVoiceSessionCalled = false;
        /** The cumulative total length of audio recorded so far */
        this.totalAudioInputDurationMs = 0;
        this.resolveSendVoiceTilesUsingVoicePacketsPromise = null;
        this.runtimeManager = runtimeManager;
        this.fdSpeechClientObserver = fdSpeechClientObserver;
        this.useFrontdoorWorkflow = useFrontdoorWorkflow;
        if (dictationUserPerceivedLatencies()) {
            this.audioPerfTracker = new AudioStreamPerfTracker(1000);
            if (sessionTelemetry) {
                this.sessionTelemetry = sessionTelemetry;
                sessionTelemetry.audioPerfTracker = this.audioPerfTracker;
            }
        }
        else {
            if (sessionTelemetry)
                this.sessionTelemetry = sessionTelemetry;
        }
        if (commandTelemetry)
            this.commandTelemetry = commandTelemetry;
        this.speechToTextProfile = speechToTextProfile;
    }
    // Start a voice session by opening the microphone and sending audio data to AL.
    // Change to call setDictationSettings, and then remove this.setDictationSettings from this method. Only call getDictationSettings
    VoiceSession.prototype.startVoiceSessionFromMicrophoneInput = function (onAudioBuffer, deviceId, commandSetManager) {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.endVoiceSessionCalled = false;
                        this.totalAudioInputDurationMs = 0;
                        return [4 /*yield*/, this.startAudioCaptureAsync(function (ap) {
                                // Stop processing microphone audio data when the caller has requested to end the voice session.
                                if (_this.endVoiceSessionCalled)
                                    return;
                                if (onAudioBuffer) {
                                    onAudioBuffer(ap.inputBuffer);
                                }
                                var sampleRate = _this.audioContext
                                    ? _this.audioContext.sampleRate
                                    : ap.inputBuffer.sampleRate;
                                var packets = encodeWAVWithoutHeader(ap.inputBuffer.getChannelData(0), sampleRate);
                                var voiceTile = AugLoopUtils.submitVoiceTile(_this.runtimeManager, packets, AUDIO_SAMPLE_RATE, _this.useFrontdoorWorkflow, DictationSettingsManager.dictationSettings, _this.responseVersion, _this.speechToTextProfile, _this.sessionTelemetry, commandSetManager === null || commandSetManager === void 0 ? void 0 : commandSetManager.getCommandSet());
                                if (dictationUserPerceivedLatencies()) {
                                    var duration = ap.inputBuffer.duration * 1000;
                                    _this.totalAudioInputDurationMs += duration;
                                    _this.audioPerfTracker.track(_this.totalAudioInputDurationMs, duration, voiceTile.seq);
                                }
                                if (_this.fdSpeechClientObserver.onAudioData) {
                                    _this.fdSpeechClientObserver.onAudioData(new Int16Array(packets.buffer));
                                }
                            }, deviceId).catch(function (error) {
                                return Promise.reject(error);
                            })];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    VoiceSession.prototype.sendVoiceTilesUsingVoicePackets = function (dataPackets, customDictationSettings) {
        var _this = this;
        return new Promise(function (resolve, reject) { return __awaiter(_this, void 0, void 0, function () {
            var offset, start, end, done, sample, packets, waitTime, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 4, , 5]);
                        if (this.resolveSendVoiceTilesUsingVoicePacketsPromise != null) {
                            reject(new Error("Please finish sending an audio file (i.e. call endVoiceSession) before sending another one."));
                        }
                        this.resolveSendVoiceTilesUsingVoicePacketsPromise = resolve;
                        offset = this._wavFileAudioChunkSize;
                        start = 0;
                        end = offset;
                        done = false;
                        _a.label = 1;
                    case 1:
                        if (!(start < dataPackets.byteLength)) return [3 /*break*/, 3];
                        // Stop sending audio packets when the caller has requested to end the voice session.
                        if (this.endVoiceSessionCalled)
                            return [3 /*break*/, 3];
                        sample = dataPackets.slice(start, end);
                        packets = new DataView(sample);
                        AugLoopUtils.submitVoiceTile(this.runtimeManager, packets, AUDIO_SAMPLE_RATE, this.useFrontdoorWorkflow, DictationSettingsManager.dictationSettings, this.responseVersion, this.speechToTextProfile, this.sessionTelemetry);
                        waitTime = (customDictationSettings &&
                            customDictationSettings.audioFileStreamDelay) ||
                            300;
                        return [4 /*yield*/, this.wait(+waitTime)];
                    case 2:
                        _a.sent(); // There is a bug for AL is sending audio chunks too fast to AL, they will be out of order
                        if (done)
                            return [3 /*break*/, 3];
                        start = end;
                        end = end + offset;
                        if (end > dataPackets.byteLength) {
                            end = dataPackets.byteLength;
                            done = true;
                        }
                        return [3 /*break*/, 1];
                    case 3:
                        if (this.resolveSendVoiceTilesUsingVoicePacketsPromise != null) {
                            this.resolveSendVoiceTilesUsingVoicePacketsPromise();
                            this.resolveSendVoiceTilesUsingVoicePacketsPromise = null;
                        }
                        return [3 /*break*/, 5];
                    case 4:
                        e_1 = _a.sent();
                        reject(e_1);
                        return [3 /*break*/, 5];
                    case 5: return [2 /*return*/];
                }
            });
        }); });
    };
    VoiceSession.prototype.resendFileFromTimeOffset = function (timeOffsetMs, durationMs) {
        if (!this.fileBytes) {
            throw new Error("Can't resend a file if startVoiceSessionFromAudioFile (or equivalent) has not been called.");
        }
        if (!this.endVoiceSessionCalled ||
            this.resolveSendVoiceTilesUsingVoicePacketsPromise != null) {
            throw new Error("Can't resend a file when we are currently sending one. End the voice session first.");
        }
        // Convert from time offset to bytes offset.
        var fileBytesOffset = (timeOffsetMs / 1000) * AUDIO_SAMPLE_RATE * (AUDIO_BITS_PER_SAMPLE / 8);
        var durationBytes = durationMs
            ? (durationMs / 1000) * AUDIO_SAMPLE_RATE * (AUDIO_BITS_PER_SAMPLE / 8)
            : undefined;
        this.fileBytes = this.fileBytes.slice(fileBytesOffset, durationBytes);
        this.endVoiceSessionCalled = false;
        return this.sendVoiceTilesUsingVoicePackets(this.fileBytes);
    };
    VoiceSession.prototype.startVoiceSessionFromAudioFileFromServer = function (dataPackets, customDictationSettings) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                this.fdSpeechClientObserver.onSpeechRecognitionStarted();
                this.fileBytes = dataPackets;
                return [2 /*return*/, this.sendVoiceTilesUsingVoicePackets(this.fileBytes, customDictationSettings)];
            });
        });
    };
    // Reads a the contents of a File object and sends them to AL.
    VoiceSession.prototype.readAndSendFile = function (file, decodeAudio, onAudioDecoded) {
        var _this = this;
        if (decodeAudio === void 0) { decodeAudio = false; }
        var fileReader = new FileReader();
        var promise = new Promise(function (resolve, reject) {
            fileReader.onload = function () { return __awaiter(_this, void 0, void 0, function () {
                var decodingStartTime, audioContext, audioBuffer, audioDuration, e_2;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            _a.trys.push([0, 4, , 5]);
                            this.fileBytes = fileReader.result;
                            if (!decodeAudio) return [3 /*break*/, 2];
                            decodingStartTime = Date.now();
                            audioContext = new OfflineAudioContext(1 /*numberOfChannels*/, 1 /*length*/, AUDIO_SAMPLE_RATE);
                            if (audioContext.decodeAudioData == null) {
                                throw new Error("Browser does not support decodeAudioData");
                            }
                            return [4 /*yield*/, getDecodeAudioDataPromise(this.fileBytes, audioContext)];
                        case 1:
                            audioBuffer = _a.sent();
                            this.fileBytes = encodeWAVWithoutHeader(audioBuffer.getChannelData(0), AUDIO_SAMPLE_RATE).buffer;
                            audioDuration = audioBuffer.duration;
                            if (onAudioDecoded) {
                                onAudioDecoded(Date.now() - decodingStartTime, audioDuration);
                            }
                            _a.label = 2;
                        case 2: return [4 /*yield*/, this.sendVoiceTilesUsingVoicePackets(this.fileBytes)];
                        case 3:
                            _a.sent();
                            resolve();
                            return [3 /*break*/, 5];
                        case 4:
                            e_2 = _a.sent();
                            reject(e_2);
                            return [3 /*break*/, 5];
                        case 5: return [2 /*return*/];
                    }
                });
            }); };
            fileReader.onabort = function () {
                reject(new Error("Aborted fileReader"));
            };
            fileReader.onerror = function () {
                reject(fileReader.error);
            };
        });
        fileReader.readAsArrayBuffer(file);
        return promise;
    };
    // Start capturing audio from wav file.
    // Currently used by Transcribe for the File Upload feature.
    VoiceSession.prototype.startVoiceSessionFromAudioFile = function (file, decodeAudio, onAudioDecoded) {
        if (decodeAudio === void 0) { decodeAudio = false; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                this.fdSpeechClientObserver.onSpeechRecognitionStarted();
                return [2 /*return*/, this.readAndSendFile(file, decodeAudio, onAudioDecoded)];
            });
        });
    };
    // Stop a voice session.
    VoiceSession.prototype.stopVoiceSession = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.stopMicrophoneInputAsync()];
                    case 1:
                        _a.sent();
                        this.endVoiceSession();
                        return [2 /*return*/];
                }
            });
        });
    };
    // Send signal to end the voice session in the AL backend.
    VoiceSession.prototype.endVoiceSession = function () {
        // Send empty voice tile with endVoiceSession = true
        AugLoopUtils.endAudioProcessor(this.runtimeManager, this.useFrontdoorWorkflow, DictationSettingsManager.dictationSettings, this.sessionTelemetry);
        this.endVoiceSessionCalled = true;
        // Immediately resolve sendVoiceTilesUsingVoicePackets promise.
        if (this.resolveSendVoiceTilesUsingVoicePacketsPromise != null) {
            this.resolveSendVoiceTilesUsingVoicePacketsPromise();
            this.resolveSendVoiceTilesUsingVoicePacketsPromise = null;
        }
    };
    /**
     * Stop capturing microphone input.
     * Note:
     *  This function will be run before/between audio capturing if SpeechToTextClient/VoiceCommandingClient get unexpected error.
     *  And this.microphoneSessionState and this.audioContext will be null in this case.
     *
     */
    VoiceSession.prototype.stopMicrophoneInputAsync = function () {
        return __awaiter(this, void 0, void 0, function () {
            var err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.microphoneSessionState) {
                            this.microphoneSessionState.mediaStream
                                .getAudioTracks()
                                .map(function (audioTrack) {
                                audioTrack.stop();
                            });
                            this.microphoneSessionState = null;
                        }
                        if (!(this.audioContext && this.audioContext.state !== "closed")) return [3 /*break*/, 4];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.audioContext.close()];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        err_1 = _a.sent();
                        ULS.sendTraceTag(0x1f85f118 /* tag_575ey */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, "Error closing audioContext: ".concat(err_1));
                        // swallow invalid state errors, re-throw anything else
                        if (err_1.name !== "InvalidStateError") {
                            throw err_1;
                        }
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    // Start capturing audio from the microphone.
    VoiceSession.prototype.startAudioCaptureAsync = function (onAudioCapturedAndProcessed, deviceId) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function () {
            var startTime, micSuccess, _c, context, sessionState, err_2, parsedError, micDuration;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        startTime = Date.now();
                        micSuccess = false;
                        _d.label = 1;
                    case 1:
                        _d.trys.push([1, 7, 8, 9]);
                        return [4 /*yield*/, this.createAudioContextAndMicrophoneSessionState(deviceId)];
                    case 2:
                        _c = _d.sent(), context = _c.context, sessionState = _c.sessionState;
                        micSuccess = true;
                        (_a = this.sessionTelemetry) === null || _a === void 0 ? void 0 : _a.setRecordingStartTime(Date.now());
                        (_b = this.commandTelemetry) === null || _b === void 0 ? void 0 : _b.setRecordingStartTime(Date.now());
                        if (!!this.endVoiceSessionCalled) return [3 /*break*/, 5];
                        this.audioContext = context;
                        this.microphoneSessionState = sessionState;
                        sessionState.processor.onaudioprocess = onAudioCapturedAndProcessed;
                        sessionState.source.connect(sessionState.processor);
                        sessionState.processor.connect(context.destination);
                        if (!!window.self.document.hasFocus()) return [3 /*break*/, 4];
                        // if a user just granted mic permission by clicking on some UX outside the page, focus may be lost.
                        // so, we need to wait a little bit for focus to naturally return or else dictation won't be allowed in onSpeechRecognitionStarted
                        return [4 /*yield*/, this.waitForWindowFocus(20)];
                    case 3:
                        // if a user just granted mic permission by clicking on some UX outside the page, focus may be lost.
                        // so, we need to wait a little bit for focus to naturally return or else dictation won't be allowed in onSpeechRecognitionStarted
                        _d.sent();
                        _d.label = 4;
                    case 4:
                        this.fdSpeechClientObserver.onSpeechRecognitionStarted();
                        return [3 /*break*/, 6];
                    case 5:
                        ULS.sendTraceTag(0x222d430c /* tag_8lumm */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, "Audio Capture is cancelled due to VoiceSession closed");
                        _d.label = 6;
                    case 6: return [3 /*break*/, 9];
                    case 7:
                        err_2 = _d.sent();
                        parsedError = this.parseMicrophoneErrorMessage(err_2);
                        this.fdSpeechClientObserver.onError(parsedError);
                        // If parsing didn't succeed, return the original error back to client
                        if (parsedError === MicrophoneError.ErrorAudioCaptureAsync) {
                            return [2 /*return*/, Promise.reject(err_2)];
                        }
                        return [2 /*return*/, Promise.reject(parsedError)];
                    case 8:
                        micDuration = Date.now() - startTime;
                        ULS.sendTraceTag(0x2328f4a2 /* tag_9kps8 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Info, "Mic enablement - success: ".concat(micSuccess, ", duration: ").concat(micDuration));
                        return [7 /*endfinally*/];
                    case 9: return [2 /*return*/];
                }
            });
        });
    };
    /** Returns a promise that resolves once we have focus or after a given number of attempts */
    VoiceSession.prototype.waitForWindowFocus = function (max) {
        if (max === void 0) { max = 20; }
        var startTime = Date.now();
        var count = max;
        return new Promise(function (resolve, _reject) {
            var checkFocus = function () {
                count -= 1;
                if (window.self.document.hasFocus() || count < 0) {
                    var duration = Date.now() - startTime;
                    ULS.sendTraceTag(0x210185c6 /* tag_7ayxg */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Info, "waitForWindowFocus duration: ".concat(duration, ", count: ").concat(count, ", window has focus: ").concat(window.self.document.hasFocus()));
                    // either we have focus or exceeded the count limit
                    resolve();
                }
                else {
                    // we do not have focus, try again shortly
                    setTimeout(checkFocus, 50);
                }
            };
            checkFocus();
        });
    };
    /** Parses the raw error/message returning an expected error code */
    VoiceSession.prototype.parseMicrophoneErrorMessage = function (err) {
        // this is effectively a clone of getMicrophoneErrorMessage from SpeechObserverController.cs but
        // the return value needs to be expected in the big switch statement of OnError in FrontdoorSpeechClientObserver.cs
        // each if here tests expected error prefixes from Firefox or Chrome respectively
        var msg = stringifyException(err);
        if (msg.startsWith("NotAllowedError") ||
            msg.endsWith("PermissionDeniedError") ||
            msg.startsWith("PermissionDeniedError")) {
            return MicrophoneError.ErrorAudioPermissionDeniedError;
        }
        if (msg.endsWith("NotFoundError") ||
            msg.startsWith("NotFoundError") ||
            msg.startsWith("DevicesNotFoundError")) {
            return MicrophoneError.ErrorAudioDevicesNotFoundError;
        }
        if (msg.endsWith("MediaDeviceNotSupported") ||
            msg.startsWith("NotSupportedError") ||
            msg.startsWith("NotReadableError") ||
            msg.startsWith("TrackStartError")) {
            return MicrophoneError.ErrorAudioNotReadableError;
        }
        // if no match is found, log it and go with the generic message
        ULS.sendTraceTag(0x2018929c /* tag_6gjk2 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Error, "Unexpected microphone error: ".concat(msg, " (").concat(err === null || err === void 0 ? void 0 : err.name, "-").concat(err === null || err === void 0 ? void 0 : err.message, ")"));
        return MicrophoneError.ErrorAudioCaptureAsync;
    };
    VoiceSession.prototype.wait = function (time) {
        return new Promise(function (resolve) {
            setTimeout(function () {
                resolve();
            }, time);
        });
    };
    VoiceSession.prototype.createAudioContextAndMicrophoneSessionState = function (deviceId) {
        return __awaiter(this, void 0, void 0, function () {
            var timeoutId, audioConstraints, mediaStream, AudioContext_1, audioContext;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        timeoutId = 0;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, , 3, 4]);
                        // Trigger some UX if we're still waiting on getUserMedia after 2 seconds (meaning, we must be waiting to for microphone permission to be given by the user)
                        timeoutId = window.setTimeout(function () {
                            timeoutId = 0;
                            if (_this.fdSpeechClientObserver.showPermissionPrompt) {
                                _this.fdSpeechClientObserver.showPermissionPrompt();
                            }
                        }, 2000);
                        audioConstraints = deviceId
                            ? { deviceId: deviceId }
                            : true;
                        return [4 /*yield*/, navigator.mediaDevices.getUserMedia({
                                audio: audioConstraints,
                            })];
                    case 2:
                        mediaStream = _a.sent();
                        AudioContext_1 = window.AudioContext || window.webkitAudioContext;
                        if (AudioContext_1 === null) {
                            return [2 /*return*/, Promise.reject("Browser doesn't support AudioContext")];
                        }
                        audioContext = new AudioContext_1();
                        /* Buffer sizes to audio durations
                            256     16ms
                            512     32ms
                           1024     64ms
                           2048    128ms
                           4096    256ms
                           8192    512ms
                          16384   1024ms
                        */
                        return [2 /*return*/, {
                                context: audioContext,
                                sessionState: {
                                    mediaStream: mediaStream,
                                    source: audioContext.createMediaStreamSource(mediaStream),
                                    processor: audioContext.createScriptProcessor(16384 /*bufferSize*/, 1 /*numberOfInputChannels*/, 1 /*numberOfOutputChannels*/),
                                },
                            }];
                    case 3:
                        // ensure we cancel this timeout wether we succeed or fail
                        if (timeoutId > 0) {
                            window.clearTimeout(timeoutId);
                        }
                        return [7 /*endfinally*/];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    return VoiceSession;
}());
export { VoiceSession };
//# sourceMappingURL=VoiceSession.js.map